import React, { useEffect, useState } from "react";
import Commend3Modal, {
  Commend3ModalInput,
  Commend3ModalLabel,
  Commend3PrimaryButton,
  Commend3SecondaryButton,
} from "./Commend3Modal";
import {
  CREATE_MODEL_NAME_PLACEHOLDER,
  CREATE_MODEL_TITLE,
  MODEL_TYPE_CO_SELLING,
  MODEL_TYPE_OPTIONS,
} from "../Commend3Constants";

type CreateModelDialogProps = {
  isOpen: boolean;
  creating: boolean;
  onClose: () => void;
  onConfirm: (name: string, type: string) => void;
};

const CreateModelDialog = ({
  isOpen,
  creating,
  onClose,
  onConfirm,
}: CreateModelDialogProps) => {
  const [name, setName] = useState<string>("");
  const [type, setType] = useState<string>(MODEL_TYPE_CO_SELLING);
  const [invalid, setInvalid] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setType(MODEL_TYPE_CO_SELLING);
      setInvalid(false);
    }
  }, [isOpen]);

  const trimmed = name.trim();

  const handleCreate = () => {
    if (!trimmed) {
      setInvalid(true);
      return;
    }
    onConfirm(trimmed, type);
  };

  return (
    <Commend3Modal
      isOpen={isOpen}
      title={CREATE_MODEL_TITLE}
      onClose={onClose}
      footer={
        <>
          <Commend3SecondaryButton
            id="commend3-create-cancel"
            onClick={onClose}
            disabled={creating}
          >
            Cancel
          </Commend3SecondaryButton>
          <Commend3PrimaryButton
            id="commend3-create-confirm"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? "Creating…" : "Create"}
          </Commend3PrimaryButton>
        </>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-3.5">
        <div>
          <Commend3ModalLabel>Model name</Commend3ModalLabel>
          <Commend3ModalInput
            id="commend3-create-name"
            value={name}
            placeholder={CREATE_MODEL_NAME_PLACEHOLDER}
            invalid={invalid}
            disabled={creating}
            onChange={(next) => {
              setName(next);
              setInvalid(false);
            }}
          />
        </div>

        <div>
          <Commend3ModalLabel>Model type</Commend3ModalLabel>
          <div className="tw-flex tw-flex-col tw-gap-2">
            {MODEL_TYPE_OPTIONS.map((option) => {
              const on = type === option.value;
              return (
                <div
                  key={option.value}
                  id={`commend3-create-type-${option.value}`}
                  className="tw-flex tw-items-start tw-gap-[11px] tw-rounded-lg tw-px-[13px] tw-py-3"
                  style={{
                    border: on ? "1.5px solid #030562" : "0.5px solid #E3E5EC",
                    cursor: creating ? "not-allowed" : "pointer",
                    opacity: creating ? 0.6 : 1,
                  }}
                  onClick={() => !creating && setType(option.value)}
                >
                  <div
                    className="tw-mt-[3px] tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-rounded-full"
                    style={{
                      border: `1.5px solid ${on ? "#030562" : "#B4B9D4"}`,
                      background: on ? "#030562" : "#fff",
                      boxShadow: on ? "inset 0 0 0 2px #fff" : "none",
                    }}
                  />
                  <div>
                    <div className="tw-text-[13px] tw-font-medium">{option.label}</div>
                    <div
                      className="tw-mt-[3px] tw-text-[12px] tw-text-[#59596E]"
                      style={{ lineHeight: 1.5 }}
                    >
                      {option.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Commend3Modal>
  );
};

export default CreateModelDialog;
